import React, { useState, useEffect } from "react";
import { Box, Flex, Text, Stack, Loader, ThemeIcon } from "@mantine/core";
import axios from "axios";
import styled from "styled-components";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import sortGalleryEfforts from "@util/sortGalleryEfforts";
import { useMicrositeData } from "@components/Microsite/useMicrositeData";
import { getPlacementImage } from "@components/Asset/helpers";
import { placementDefs } from "@components/Asset/placementDefs";

export default function EffortPickerDeux({
  reqData,
  beforeFetch = () => {},
  showLogo = true,
  location,
}) {
  const [efforts, setEfforts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    setLoading(true);
    axios
      .post(`/microsite/additional-efforts/`, {
        ...reqData,
      })
      .then(({ data }) => {
        setEfforts(data.response);
        // setEfforts([]);
      })
      .catch((err) => {
        setEfforts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const sortedEfforts = sortGalleryEfforts(efforts);

  return (
    <div>
      <ViewHeader location={location} showLogo={showLogo} />
      {loading && (
        <Flex justify="center" align="center" p="xl">
          <Loader size="xl" variant="dots" color="gray" />
        </Flex>
      )}
      <Stack gap="xs">
        {!loading && sortedEfforts.length === 0 && (
          <Flex justify="center" align="center" p="xl">
            <Text size="sm" c="white" fw={600} align="center">
              {t("microsite:effort_picker_no_efforts")}
            </Text>
          </Flex>
        )}
        {sortedEfforts.map((item, index) => (
          <Item
            key={index}
            badgeText={`${index + 1} of ${sortedEfforts.length}`}
            entered={item.entered}
            title={item.title}
            dates={getDateString(
              item.gallery_start_date_formatted,
              item.gallery_end_date_formatted
            )}
            // url={
            //   item.variety === 6
            //     ? `/ride-challenge-entry/${item.url_code}`
            //     : `/entry/${item.url_code}`
            // }
            url={`/entry/${item.url_code}`}
            description={item.description}
            recurring={item.recurring_engagement === 1}
            item={item}
          />
        ))}
      </Stack>
    </div>
  );
}

const Item = ({
  images,
  badgeText,
  title,
  dates,
  url,
  description,
  recurring,
  entered,
  item,
}) => {
  const { fetchAppData, setAppData } = useMicrositeData();
  const { t } = useTranslation();
  const image = getPlacementImage(
    item.configuration,
    item.assets,
    placementDefs.featured
  );
  return (
    <div>
      <StyledItem
        onClick={() => {
          setAppData({
            ...item,
            campaign_effort_id: item.id,
            app_configuration: item.app_configuration,
          });
        }}
      >
        <Box style={{ position: "relative" }}>
          <Flex gap={0} align="center">
            <Box style={{ padding: 0, position: "relative" }}>
              <img src={image} />
              {entered && (
                <div className="entered-image-overlay">
                  <ThemeIcon color="green" size="lg" radius="xl">
                    <IconCheck size={24} stroke={4} />
                  </ThemeIcon>
                </div>
              )}
            </Box>
            <Stack
              gap={2}
              style={{ flexGrow: 1, padding: "12px 12px 12px 12px" }}
            >
              <Text c="white" fw={600} size="md" lineClamp={2} lh={1.25}>
                {title}
              </Text>
              {dates && (
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {dates}
                </Text>
              )}
              {entered && recurring && (
                <Text fw={600} size="xs" c="dimmed">
                  {t("microsite:effort_picker_recurring_text")}
                </Text>
              )}
            </Stack>
          </Flex>
        </Box>
      </StyledItem>
    </div>
  );
};

const ViewHeader = ({ location, showLogo }) => {
  const { t } = useTranslation();
  return (
    <div>
      {location.logo_url && showLogo && (
        <Flex justify="center" mb="md">
          <img
            src={location.logo_url}
            style={{ width: "125px", maxHeight: "100px", objectFit: "contain" }}
          />
        </Flex>
      )}
      <Text align="center" mb="md" c="white" size="lg" px="md" fw={600}>
        {t("microsite:effort_picker_title")}
        <br />
        {t("microsite:effort_picker_subtitle", { location: location.name })}
      </Text>
    </div>
  );
};

const imageSize = {
  width: "90px",
  height: "90px",
};

const StyledItem = styled.div`
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px;
  overflow: hidden;
  background: var(--mantine-color-dark-6);
  padding: 0;
  border: 2px solid var(--mantine-color-dark-5);

  img {
    width: ${imageSize.width};
    object-fit: cover;
    height: ${imageSize.height};
    border-radius: 8px 0 0 8px;
    transition: transform 0.2s ease;
    display: block;
    /* max-height: 90px; */
  }

  .mantine-Badge-root {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .entered-image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: ${imageSize.width};
    height: ${imageSize.height};
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
    flex-direction: column;
  }
`;

function getDateString(startDate, endDate) {
  if (!startDate || !endDate) return "";

  const startParts = startDate.split(" ");
  const endParts = endDate.split(" ");

  if (
    startParts.length >= 3 &&
    endParts.length >= 3 &&
    startParts[startParts.length - 1] === endParts[endParts.length - 1]
  ) {
    const startWithoutYear = startParts
      .slice(0, -1)
      .join(" ")
      .replace(/,\s*$/, "");
    return `${startWithoutYear}-${endDate}`;
  }

  return `${startDate}-${endDate}`;
}
